import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, Button, Badge } from '../components/Shared';
import { approvals } from '../data/mock';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiCircle, FiArrowRight, FiX, FiCheck } from 'react-icons/fi';

const Approvals = () => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [checkedDocs, setCheckedDocs] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);

  const requiredDocs = [
    { id: 'd1', name: 'Signed W-9 Form' },
    { id: 'd2', name: 'Master Service Agreement (MSA)' },
    { id: 'd3', name: 'Security & Compliance Review' },
    { id: 'd4', name: 'Banking Details Verification' }
  ];

  const handleCheck = (id) => {
    if (checkedDocs.includes(id)) {
      setCheckedDocs(checkedDocs.filter(d => d !== id));
    } else {
      setCheckedDocs([...checkedDocs, id]);
    }
  };

  const handleApprove = () => {
    setCompletedTasks([...completedTasks, selectedTask.id]);
    setSelectedTask(null);
    setCheckedDocs([]);
  };

  const activeTasks = approvals.filter(a => !completedTasks.includes(a.id));

  return (
    <div className="p-8 max-w-7xl mx-auto flex gap-8 h-[calc(100vh-5rem)]">
      
      {/* Workflow Inbox */}
      <div className={`flex-1 transition-all duration-300 ${selectedTask ? 'hidden lg:block lg:w-1/2' : 'w-full'}`}>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white">Approval Workflows</h1>
          <p className="text-slate-400 mt-1">Review and process vendor requests.</p>
        </div>

        <div className="space-y-4">
          <AnimatePresence>
            {activeTasks.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center h-64 text-slate-500">
                <SafeIcon icon={FiCheckCircle} className="text-4xl mb-4 text-emerald-500/50" />
                <p>All caught up! No pending approvals.</p>
              </motion.div>
            ) : (
              activeTasks.map(task => (
                <motion.div 
                  layout
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, scale: 0.95 }}
                  key={task.id}
                  onClick={() => { setSelectedTask(task); setCheckedDocs([]); }}
                >
                  <Card className={`p-5 cursor-pointer transition-all ${selectedTask?.id === task.id ? 'border-yellow-400 shadow-[0_0_20px_rgba(250,204,21,0.1)]' : 'hover:border-slate-600'}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-white text-lg">{task.vendor}</h3>
                          <Badge type="warning">Requires Action</Badge>
                        </div>
                        <p className="text-slate-400 text-sm">{task.task}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 group-hover:text-yellow-400 group-hover:bg-yellow-400/10 transition-colors">
                        <SafeIcon icon={FiArrowRight} />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-800 flex justify-between text-xs text-slate-500">
                      <span>Submitted by {task.submittedBy}</span>
                      <span>{task.date}</span>
                    </div>
                  </Card>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Interactive Review Panel */}
      <AnimatePresence>
        {selectedTask && (
          <motion.div 
            initial={{ opacity: 0, x: 20 }} 
            animate={{ opacity: 1, x: 0 }} 
            exit={{ opacity: 0, x: 20 }}
            className="flex-1 lg:w-1/2 h-full flex flex-col"
          >
            <Card className="flex-1 flex flex-col border-yellow-400/30">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-[#0A0F1C]/50">
                <div>
                  <h2 className="text-xl font-bold text-white">Document Checklist</h2>
                  <p className="text-sm text-yellow-400 mt-1">{selectedTask.vendor} Onboarding</p>
                </div>
                <button onClick={() => setSelectedTask(null)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors lg:hidden">
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              <div className="p-6 flex-1 overflow-y-auto space-y-4">
                <p className="text-sm text-slate-400 mb-6">Please verify the following documents have been submitted and reviewed before final approval.</p>
                
                {requiredDocs.map(doc => {
                  const isChecked = checkedDocs.includes(doc.id);
                  return (
                    <div 
                      key={doc.id} 
                      onClick={() => handleCheck(doc.id)}
                      className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                        isChecked ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-[#0A0F1C] border-slate-700 hover:border-slate-500'
                      }`}
                    >
                      <div className={`text-xl ${isChecked ? 'text-emerald-400' : 'text-slate-600'}`}>
                        <SafeIcon icon={isChecked ? FiCheckCircle : FiCircle} />
                      </div>
                      <span className={`font-medium ${isChecked ? 'text-emerald-100' : 'text-slate-300'}`}>{doc.name}</span>
                    </div>
                  )
                })}
              </div>

              <div className="p-6 border-t border-slate-800 bg-[#0A0F1C]/80 backdrop-blur">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-slate-400">Progress</span>
                  <span className="text-sm font-bold text-yellow-400">{checkedDocs.length} / {requiredDocs.length}</span>
                </div>
                <div className="w-full h-2 bg-slate-800 rounded-full mb-6 overflow-hidden">
                  <motion.div 
                    className="h-full bg-yellow-400" 
                    initial={{ width: 0 }}
                    animate={{ width: `${(checkedDocs.length / requiredDocs.length) * 100}%` }}
                  />
                </div>
                
                <div className="flex gap-4">
                  <Button variant="secondary" className="flex-1" onClick={() => setSelectedTask(null)}>Cancel</Button>
                  <Button 
                    variant="primary" 
                    className="flex-1 flex justify-center items-center gap-2"
                    disabled={checkedDocs.length !== requiredDocs.length}
                    onClick={handleApprove}
                  >
                    <SafeIcon icon={FiCheck} /> Final Approval
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Approvals;